import { URITemplateParams } from '@superdispatch/uri';
import {
  QueryClient,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  keepPreviousData as placeholderData,
} from '@tanstack/react-query';
import { APISchema, castAPIData } from 'shared/api/APISchema';
import { APIResponse } from 'shared/api/CarrierAPIClient';

export type APIQueryInput =
  | string[]
  | readonly [domain: string, resource: string, variables?: URITemplateParams];

export type APIQueryOptions<TData> = Omit<
  UseQueryOptions<TData>,
  'queryFn' | 'queryKey' | 'queryHash' | 'queryKeyHashFn' | 'isDataEqual'
> & { keepPreviousData?: boolean };

export interface UseAPIQueryOptions<TData> extends APIQueryOptions<TData> {
  schema?: APISchema<TData>;
  normalize?: (response: unknown) => TData;
  keepPreviousData?: boolean;
}

export type APIQueryResult<TData> = UseQueryResult<TData>;

export function useAPIQuery<TData>(
  queryKey: APIQueryInput,
  query: () => Promise<APIResponse<TData>>,
  {
    schema,
    normalize,
    keepPreviousData,
    ...options
  }: UseAPIQueryOptions<TData> = {},
): APIQueryResult<TData> {
  return useQuery({
    queryKey,
    queryFn: () =>
      query().then(({ data }) => {
        if (schema) data = castAPIData(schema, data);
        if (normalize) data = normalize(data);
        return data;
      }),
    placeholderData: keepPreviousData ? placeholderData : undefined,
    ...options,
  });
}

export type APIQueryDataUpdater<TData> = (data: TData) => TData;

export function setAPIQueryData<TData>(
  queryClient: QueryClient,
  input: APIQueryInput,
  updater: APIQueryDataUpdater<TData>,
): void {
  const data = queryClient.getQueryData<TData>(input);
  if (data) queryClient.setQueryData(input, updater(data));
}
